import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DateRange } from "../components/DateRangePicker";

export interface UseMonthWindowOptions {
  resetTarget?: Date;
  ready?: boolean;
  extraResetDeps?: unknown[];
}

export interface UseMonthWindowResult {
  viewEndDate: Date;
  monthLabels: string[];
  canGoNext: boolean;
  periodText: string;
  goToPrevMonth: () => void;
  goToNextMonth: () => void;
  goToPrevYear: () => void;
  goToNextYear: () => void;
}

export const useMonthWindow = (
  dateRange: DateRange,
  options?: UseMonthWindowOptions,
): UseMonthWindowResult => {
  const { t } = useTranslation();
  const resetTarget = options?.resetTarget ?? new Date();
  const ready = options?.ready ?? true;
  const extraResetDeps = options?.extraResetDeps ?? [];

  const [viewEndDate, setViewEndDate] = useState(new Date());
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (ready && !hasInitialized) {
      setViewEndDate(resetTarget);
      setHasInitialized(true);
    }
  }, [ready, hasInitialized]);

  useEffect(() => {
    if (hasInitialized) {
      setViewEndDate(resetTarget);
    }
  }, [dateRange, hasInitialized, ...extraResetDeps]);

  const monthLabels = useMemo(
    () => [
      t("pages.statistics.months.january"),
      t("pages.statistics.months.february"),
      t("pages.statistics.months.march"),
      t("pages.statistics.months.april"),
      t("pages.statistics.months.may"),
      t("pages.statistics.months.june"),
      t("pages.statistics.months.july"),
      t("pages.statistics.months.august"),
      t("pages.statistics.months.september"),
      t("pages.statistics.months.october"),
      t("pages.statistics.months.november"),
      t("pages.statistics.months.december"),
    ],
    [t],
  );

  const goToPrevMonth = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const next = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const goToPrevYear = () => {
    setViewEndDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  };

  const goToNextYear = () => {
    const next = new Date(viewEndDate.getFullYear() + 1, viewEndDate.getMonth(), 1);
    if (next <= new Date()) {
      setViewEndDate(next);
    }
  };

  const canGoNext =
    new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() + 1, 1) <= new Date();

  const periodText = useMemo(() => {
    const startDate = new Date(viewEndDate.getFullYear(), viewEndDate.getMonth() - 11, 1);
    const startStr = `${monthLabels[startDate.getMonth()].slice(0, 3)} ${startDate.getFullYear()}`;
    const endStr = `${monthLabels[viewEndDate.getMonth()].slice(
      0,
      3,
    )} ${viewEndDate.getFullYear()}`;
    return `${startStr} - ${endStr}`;
  }, [viewEndDate, monthLabels]);

  return {
    viewEndDate,
    monthLabels,
    canGoNext,
    periodText,
    goToPrevMonth,
    goToNextMonth,
    goToPrevYear,
    goToNextYear,
  };
};
